import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import {
  collection, getDocs, query, orderBy, limit,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import StatusPill from '../../components/ui/StatusPill';
import { useTranslation } from '../../i18n/LanguageContext';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import {
  IconClipboardList,
  IconTemplate,
  IconSearch,
  IconPlus,
} from '@tabler/icons-react';

export default function Checks() {
  const { t, language } = useTranslation();
  const { orgId, userProfile, role } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterResult, setFilterResult] = useState('');

  const siteId = userProfile?.siteId;

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const [templatesSnap, machinesSnap] = await Promise.all([
          getDocs(collection(db, 'organisations', orgId, 'checkTemplates')),
          getDocs(collection(db, 'organisations', orgId, 'machines')),
        ]);

        setTemplates(templatesSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        let machineList = machinesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (role === 'manager' && siteId) machineList = machineList.filter(m => m.siteId === siteId);

        const allChecks = [];
        await Promise.all(machineList.map(async (machine) => {
          const checksSnap = await getDocs(query(
            collection(db, 'organisations', orgId, 'machines', machine.id, 'checkRecords'),
            orderBy('submittedAt', 'desc'),
            limit(20),
          ));
          checksSnap.docs.forEach(d => {
            allChecks.push({
              id: d.id,
              machineId: machine.id,
              makeLabel: machine.makeLabel,
              modelLabel: machine.modelLabel,
              registration: machine.registration,
              ...d.data(),
            });
          });
        }));

        allChecks.sort((a, b) => {
          const aDate = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt);
          const bDate = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt);
          return bDate - aDate;
        });
        setChecks(allChecks);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, siteId, role]);

  const filteredChecks = checks.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      c.makeLabel?.toLowerCase().includes(q) ||
      c.modelLabel?.toLowerCase().includes(q) ||
      c.registration?.toLowerCase().includes(q) ||
      c.submittedByName?.toLowerCase().includes(q);
    const matchResult = !filterResult || c.result === filterResult;
    return matchSearch && matchResult;
  });

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today · ' + d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday · ' + d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString(language, { day: 'numeric', month: 'short' }) + ' · ' + d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
  };

  const passed = checks.filter(c => c.result === 'pass').length;
  const failed = checks.filter(c => c.result === 'fail').length;

  const scopeLabel = (template) => {
    if (template.specificMachineId) return t('checks.scope_specific');
    if (template.machineCategories?.length) return template.machineCategories.length + ' ' + t('checks.categories');
    return t('checks.scope_all');
  };

  const TABS = [
    { key: 'templates', label: t('checks.tab_templates'), icon: IconTemplate,       count: templates.length },
    { key: 'history',   label: t('checks.tab_history'),   icon: IconClipboardList,  count: checks.length    },
  ];

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title={t('checks.title')}
          subtitle={templates.length + ' ' + t(templates.length === 1 ? 'checks.template_one' : 'checks.template_many') + ' · ' + checks.length + ' ' + t(checks.length === 1 ? 'checks.record_one' : 'checks.record_many')}
          actions={
            activeTab === 'templates' && (role === 'org_admin' || role === 'manager') ? (
              <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/checks/templates/new')}>
                {t('checks.new_template')}
              </Button>
            ) : null
          }
        />

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '0.5px solid var(--color-border-light)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <div
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '10px 16px', fontSize: 13, cursor: 'pointer',
                  color: active ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                  borderBottom: active ? '2px solid var(--color-brand)' : '2px solid transparent',
                  marginBottom: -1, transition: 'all 0.15s',
                }}
              >
                <Icon size={14} stroke={1.5} />
                {tab.label}
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{tab.count}</span>
              </div>
            );
          })}
        </div>

        {/* TEMPLATES TAB */}
        {activeTab === 'templates' && (
          <div>
            {loading ? (
              <LoadingSkeleton rows={3} card />
            ) : templates.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<IconTemplate size={32} stroke={1.5} />}
                  title={t('checks.no_templates')}
                  message={t('checks.no_templates_msg')}
                  action={(role === 'org_admin' || role === 'manager') && (
                    <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/checks/templates/new')}>
                      {t('checks.create_template')}
                    </Button>
                  )}
                />
              </Card>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                {templates.map(template => {
                  const itemCount = template.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0;
                  return (
                    <Card key={template.id} onClick={() => navigate('/checks/templates/' + template.id + '/edit')} hover>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 4 }}>{template.name}</div>
                          {template.description && (
                            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>{template.description}</div>
                          )}
                        </div>
                        <StatusPill status={template.active ? 'active' : 'archived'} />
                      </div>
                      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'var(--color-text-tertiary)', paddingTop: 10, borderTop: '0.5px solid var(--color-border-light)' }}>
                        <span style={{ textTransform: 'capitalize' }}>{template.frequency || '—'}</span>
                        <span>{scopeLabel(template)}</span>
                        <span>{itemCount} {t('checks.items')}</span>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
              {[
                { label: t('checks.stat_total'), value: checks.length, colour: 'var(--color-text-primary)' },
                { label: t('checks.stat_passed'), value: passed, colour: 'var(--color-success)' },
                { label: t('checks.stat_failed'), value: failed, colour: failed > 0 ? 'var(--color-danger)' : 'var(--color-success)' },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
                  <div style={{ fontSize: 24, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
                <IconSearch size={14} stroke={1.5} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
                <input type="text" placeholder={t('checks.search')} value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 32, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <select value={filterResult} onChange={e => setFilterResult(e.target.value)} style={{ fontSize: 13, padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', background: 'var(--color-bg-primary)', minWidth: 120 }}>
                <option value="">{t('checks.all_results')}</option>
                <option value="pass">{t('checks.pass')}</option>
                <option value="fail">{t('checks.fail')}</option>
              </select>
            </div>

            <Card padding="0">
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.8fr 60px', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
                {['machines.col_machine', 'checks.col_operator', 'checks.col_datetime', 'checks.col_result', ''].map(h => (
                  <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h ? t(h) : ''}</div>
                ))}
              </div>
              {loading ? <LoadingSkeleton rows={6} /> : filteredChecks.length === 0 ? (
                <EmptyState icon={<IconClipboardList size={32} stroke={1.5} />} title={t('checks.none_found')} message={search || filterResult ? t('checks.adjust_filters') : t('checks.none_submitted')} />
              ) : filteredChecks.map((check, i) => (
                <div
                  key={check.id}
                  onClick={() => navigate('/checks/' + check.id)}
                  style={{
                    display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.8fr 60px',
                    padding: '12px 16px',
                    borderBottom: i < filteredChecks.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                    cursor: 'pointer', alignItems: 'center',
                    background: check.result === 'fail' ? 'var(--color-danger-light)' : 'transparent',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{check.makeLabel} {check.modelLabel}</div>
                    {check.registration && (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, background: '#FAC775', color: '#412402', padding: '1px 5px', borderRadius: 2 }}>{check.registration}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{check.submittedByName || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{formatDateTime(check.submittedAt)}</div>
                  <StatusPill status={check.result || 'pending'} />
                  <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>{t('machines.view')}</div>
                </div>
              ))}
            </Card>
          </div>
        )}

      </div>
    </Layout>
  );
}